import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Clock, Layers3, User2, Loader2, ArrowUpRight, LogIn } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RegisterCourseButton } from "../components/RegisterCourseButton";
import { Reveal, Chapter } from "../components/Reveal";

const COURSE_CATEGORIES = ["All", "Language", "Linguistics", "Literature", "AI and Digital Turn in Literature"];
const LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];

const PROGRAM_META = {
  ielts: {
    title: "IELTS",
    heading: "IELTS preparation,",
    accent: "the right way.",
    description:
      "Six focused modules covering every section of the IELTS Academic test — Reading, Writing, Listening, Speaking, Grammar, and Pronunciation — taught by a specialist trainer.",
  },
  toefl: {
    title: "TOEFL",
    heading: "TOEFL iBT preparation,",
    accent: "section by section.",
    description:
      "Six modules built around all four TOEFL iBT task types plus Grammar and Pronunciation, with structured practice and expert guidance on every question type.",
  },
  "english-language": {
    title: "English Language",
    heading: "English language skills,",
    accent: "from the ground up.",
    description:
      "Six comprehensive modules — Reading, Writing, Listening, Speaking, Grammar, and Pronunciation — for learners who want to build confident, accurate English across every skill.",
  },
  "english-literature": {
    title: "English Literature",
    heading: "English literature,",
    accent: "from GCSE to university.",
    description:
      "Thirteen modules across literary forms and genres — Poetry, Novel, Short Story, English Drama, Literary Criticism — and eight literary periods from Anglo-Saxon to Postcolonial, each offered at Beginner (GCSE), Intermediate (A-Level), and Advanced (Undergraduate) level.",
  },
  "ai-digital-literature": {
    title: "AI and Digital Turn in Literature",
    heading: "AI and the digital turn",
    accent: "in literature.",
    description:
      "Two tracks — AI Tools in Literature Teaching and Research, and AI Changing Literature — exploring how AI supports literary analysis, writing, and research, and how it is transforming literary production, criticism, and readership. Every module runs at Beginner, Intermediate, and Advanced level.",
  },
};

const levelColor = (level) =>
  ({
    Beginner: "border-emerald-200 bg-emerald-50 text-emerald-700",
    Intermediate: "border-gold/40 bg-gold/10 text-gold-dark",
    Advanced: "border-forest/30 bg-forest/10 text-forest",
  }[level] || "border-border bg-secondary text-foreground/70");

function MyEnrollments({ user, program }) {
  const [enrollments, setEnrollments] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/language-courses/my/enrollments")
      .then((r) => setEnrollments((r.data || []).filter((e) => e.program === program)))
      .catch(() => setEnrollments([]));
  }, [user, program]);

  if (!user) {
    return (
      <div className="rounded-[2rem] border border-dashed border-forest/25 bg-white p-10 text-center" data-testid="lc-enrollments-login-prompt">
        <LogIn className="mx-auto h-8 w-8 text-forest/30" />
        <p className="mt-4 font-display text-lg font-bold text-forest-deep">Track your learning here</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          Log in to see the modules you're enrolled in and pick up where you left off.
        </p>
        <Link to="/login" data-testid="lc-enrollments-login-btn"
          className="mt-5 inline-block rounded-full bg-forest px-7 py-3 font-display text-sm font-bold text-bone">
          Log in
        </Link>
      </div>
    );
  }

  if (enrollments === null) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  }

  if (enrollments.length === 0) {
    return (
      <p className="rounded-[2rem] border border-dashed border-forest/25 bg-white py-12 text-center text-sm text-muted-foreground" data-testid="lc-enrollments-empty">
        You're not enrolled in any module yet — find one above.
      </p>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="lc-enrollments-list">
      {enrollments.map((e) => (
        <Link key={e.id} to={`/language-course/${e.course_id}`} data-testid={`lc-enrollment-${e.id}`}
          className="group flex items-center justify-between gap-4 rounded-3xl border border-border bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_16px_40px_rgba(10,41,30,0.09)]">
          <div>
            <p className="font-display text-base font-bold text-forest-deep">{e.course_title}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Enrolled {new Date(e.enrolled_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </p>
          </div>
          <span className="flex items-center gap-1.5 rounded-full bg-forest/5 px-4 py-2 text-xs font-bold text-forest transition-colors group-hover:bg-gold/15 group-hover:text-gold-dark">
            Continue <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </Link>
      ))}
    </div>
  );
}

export default function LanguageCoursePage({ program }) {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [category, setCategory] = useState("All");
  const [level, setLevel] = useState("All");
  const [track, setTrack] = useState("All");

  const meta = PROGRAM_META[program] || PROGRAM_META["english-language"];

  useEffect(() => {
    setCourses(null);
    setCategory("All");
    setLevel("All");
    setTrack("All");
    api
      .get(`/language-courses?program=${program}`)
      .then((r) => setCourses(r.data))
      .catch(() => setCourses([]));
  }, [program]);

  const filtered = (courses || []).filter(
    (c) => (category === "All" || c.category === category) && (level === "All" || c.level === level) &&
      (track === "All" || c.track === track)
  );
  const tracks = [...new Set((courses || []).map((c) => c.track).filter(Boolean))];

  const hubPath = {
    ielts: "/ielts-courses",
    toefl: "/toefl-courses",
    "english-language": "/english-language-courses",
    "english-literature": "/english-literature-courses",
    "ai-digital-literature": "/ai-digital-literature-courses",
  }[program];

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid={`language-courses-page-${program}`}>
      {/* Hero */}
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="lc-breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span>
              <span className="text-gold">{meta.title} Courses</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl">
              {meta.heading}{" "}
              <em className="font-editorial font-medium italic text-gold">{meta.accent}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              {meta.description}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-5">
            <Chapter index="01" label="Course modules" />
            <div className="flex flex-wrap gap-2" data-testid="lc-course-filters">
              {tracks.length > 0 && ["All", ...tracks].map((t) => (
                <button key={t} data-testid={`lc-filter-track-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setTrack(t)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    track === t ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                  }`}>
                  {t === "All" ? "All tracks" : t}
                </button>
              ))}
              {tracks.length === 0 && COURSE_CATEGORIES.map((c) => (
                <button key={c} data-testid={`lc-filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    category === c ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                  }`}>
                  {c}
                </button>
              ))}
              <span className="mx-1 hidden h-8 w-px self-center bg-border sm:block" />
              {LEVELS.map((l) => (
                <button key={l} data-testid={`lc-filter-level-${l.toLowerCase()}`}
                  onClick={() => setLevel(l)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    level === l ? "border-gold-dark bg-gold text-forest-deep" : "border-border bg-white text-foreground/60 hover:border-gold-dark/50"
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="lc-courses-grid">
          {courses === null ? (
            <div className="col-span-full flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-forest" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="col-span-full rounded-3xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="lc-courses-empty">
              No modules match those filters.
            </p>
          ) : (
            filtered.map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 0.08}>
                <Link to={`/language-course/${c.id}`} data-testid={`lc-course-card-${i}`}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_22px_55px_rgba(10,41,30,0.1)]">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">{c.track || c.category}</span>
                    <span className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${levelColor(c.level)}`}>
                      {c.level}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold leading-snug text-forest-deep">{c.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {c.description.slice(0, 110)}…
                  </p>
                  <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold-dark" />{c.duration}</span>
                    <span className="flex items-center gap-1.5"><Layers3 className="h-3.5 w-3.5 text-gold-dark" />{c.lessons} lessons</span>
                    <span className="flex items-center gap-1.5"><User2 className="h-3.5 w-3.5 text-gold-dark" />{c.instructor}</span>
                  </div>
                  <div className="mt-5 flex items-center justify-between border-t border-border pt-5">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <RegisterCourseButton course={c} courseType="language" testId={`lc-course-register-btn-${i}`} />
                      {c.price > 0 && (
                        <p className="whitespace-nowrap text-sm text-muted-foreground" data-testid={`lc-course-price-${i}`}>
                          <span className="font-display text-xl font-extrabold text-forest-deep">${c.price}</span> /lesson
                        </p>
                      )}
                    </div>
                    <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-forest transition-colors group-hover:text-gold-dark">
                      View module <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))
          )}
        </div>

        <div className="mt-16">
          <Reveal>
            <Chapter index="02" label="My learning" />
          </Reveal>
          <div className="mt-8">
            <MyEnrollments user={user} program={program} />
          </div>
        </div>
      </div>
    </div>
  );
}
